import { ChangeDetectionStrategy, Component, computed, effect, inject, OnDestroy, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AssetVideo, AssetVideoSchema, VideoCheckResponse } from 'src/app/common/models/ai-reports/shared/assets';
import { AbstractAssetComponent } from '../abstract-asset.component';
import { ChatStore } from 'src/app/services/store';
import { ProjectRefService } from 'src/app/services/project-ref.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-asset-video',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './asset-video.component.html',
  styleUrl: './asset-video.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetVideoComponent extends AbstractAssetComponent<AssetVideo> implements OnDestroy {
  private readonly chatStore = inject(ChatStore);
  private readonly projectRefService = inject(ProjectRefService);
  private readonly translate = inject(TranslateService);

  readonly tryAgain = output<string>();

  readonly videoAsset = signal<AssetVideo>(null!);
  readonly checking = signal(false);
  readonly checkError = signal<string | null>(null);

  private es: EventSource | null = null;
  private lastInput: AssetVideo | null = null;

  constructor() {
    super();
    // Lists track by asset.id, so on regeneration this component instance is REUSED
    // with a new asset object (FT-901). React to input changes instead of ngOnInit —
    // but ignore re-emissions of unchanged data, so a fresher SSE/check update held
    // in videoAsset is never clobbered by a stale parent input.
    effect(() => {
      const a = this.asset();
      const prev = this.lastInput;
      if (prev && prev.id === a.id && prev.status === a.status && prev.modifiedAt === a.modifiedAt) {
        return;
      }
      this.lastInput = a;
      this.videoAsset.set(a);
      this.checkError.set(null);
      this.closeWatch();
      // Thumbnail cards only render a status icon — no SSE connection needed per card.
      if (a.status === 'pending' && this.mode() !== 'thumbnail') {
        this.openWatch();
      }
    });
  }

  ngOnDestroy(): void {
    this.closeWatch();
  }

  readonly thumbnailUrl = computed(() => {
    const path = this.videoAsset().thumbnailStoragePath;
    return path ? `${environment.apiBaseUrl}/assets/storage?path=${encodeURIComponent(path)}` : null;
  });

  // Native <video>/<a> elements cannot send auth headers, so use the cookie-based
  // streaming endpoint (same as image assets); thread context comes from cookies.
  videoSrc(): string {
    const a = this.videoAsset();
    return `${environment.apiBaseUrl}${environment.endpoint.assets.image}/${a.id}`;
  }

  onTryAgain(): void {
    this.tryAgain.emit(this.videoAsset().prompt);
  }

  checkStatus(): void {
    const a = this.videoAsset();
    const projectId = this.projectRefService.getCurrentProject()?.familyProjectId;
    const threadId = this.chatStore.thread_id();
    if (!projectId || !threadId) {
      this.checkError.set(this.translate.instant('aiReport.video_context_missing'));
      return;
    }
    const url = `${environment.apiBaseUrl}/video/check/${projectId}/${threadId}/${a.id}`;

    this.checking.set(true);
    this.checkError.set(null);
    fetch(url, { method: 'POST', credentials: 'include', signal: AbortSignal.timeout(15000) })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((res: VideoCheckResponse) => {
        // The asset may have been regenerated under the same id while this check
        // was in flight — the input-reactive effect already reset videoAsset for
        // the new attempt. Applying this now-stale response would clobber that
        // reset state with a result belonging to the previous generation.
        const stillSameAttempt = this.videoAsset().id === a.id && this.videoAsset().modifiedAt === a.modifiedAt;
        if (res.asset && stillSameAttempt) this.videoAsset.set(res.asset);
      })
      .catch((err: unknown) => {
        console.error('Video status check failed:', err);
        this.checkError.set(this.translate.instant('aiReport.video_check_failed'));
      })
      .finally(() => this.checking.set(false));
  }

  private openWatch(): void {
    const a = this.videoAsset();
    const projectId = this.projectRefService.getCurrentProject()?.familyProjectId;
    const threadId = this.chatStore.thread_id();
    // Without context the URL would be /assets/undefined/… — skip the watch;
    // the manual Check status button remains as fallback.
    if (!projectId || !threadId) return;
    const url = `${environment.apiBaseUrl}/assets/${projectId}/${threadId}/${a.id}/watch`;

    this.es = new EventSource(url, { withCredentials: true });
    this.es.onmessage = (e: MessageEvent) => {
      try {
        const updated = AssetVideoSchema.parse(JSON.parse(e.data));
        this.videoAsset.set(updated);
      } catch (err) {
        console.error('Malformed video watch event:', err);
      }
      this.closeWatch();
    };
    this.es.onerror = () => this.closeWatch();
  }

  private closeWatch(): void {
    this.es?.close();
    this.es = null;
  }
}
